import React, { useState, useRef, useEffect } from 'react';
import { useNotifications } from '../../context/NotificationContext';
import { formatDistanceToNow } from 'date-fns';

// Inject styles once
(function injectStyles() {
  if (typeof document === 'undefined' || document.getElementById('nb-styles')) return;
  const s = document.createElement('style');
  s.id = 'nb-styles';
  s.textContent = `
    @keyframes nb-drop { from { opacity:0; transform:translateY(-8px) scale(0.97); } to { opacity:1; transform:translateY(0) scale(1); } }
    @keyframes nb-pulse { 0%,100% { box-shadow:0 0 0 0 rgba(239,68,68,0.5); } 50% { box-shadow:0 0 0 5px rgba(239,68,68,0); } }
    @keyframes nb-bell { 0%,100% { transform:rotate(0); } 20% { transform:rotate(-12deg); } 40% { transform:rotate(12deg); } 60% { transform:rotate(-6deg); } 80% { transform:rotate(6deg); } }
    .nb-item:hover { background: rgba(255,255,255,0.06) !important; }
    .nb-tab:hover { background: rgba(255,255,255,0.08) !important; }
    .nb-bell-btn:hover { background: rgba(255,255,255,0.22) !important; border-color: rgba(255,255,255,0.4) !important; }
    .nb-mark-btn:hover { color: #a5b4fc !important; }
    ::-webkit-scrollbar { width: 4px; }
    ::-webkit-scrollbar-track { background: transparent; }
    ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.15); border-radius: 2px; }
  `;
  document.head.appendChild(s);
})();

const PRIORITY_COLOR = { low: '#10b981', medium: '#3b82f6', high: '#f59e0b', urgent: '#ef4444' };
const TYPE_ICON = {
  complaint_verified: '✓', complaint_resolved: '✅', complaint_status: '📋',
  deadline_reminder: '⏰', escalation: '⚠', assignment: '📌',
  new_complaint: '📝', report_submitted: '📊', report_reviewed: '📋',
};
const TYPE_COLOR = {
  complaint_verified: '#10b981', complaint_resolved: '#10b981', complaint_status: '#6366f1',
  deadline_reminder: '#f59e0b', escalation: '#ef4444', assignment: '#06b6d4',
  new_complaint: '#8b5cf6', report_submitted: '#3b82f6', report_reviewed: '#3b82f6',
};

function NotificationBell() {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('unread');
  const dropdownRef = useRef(null);
  const btnRef = useRef(null);
  const [dropPos, setDropPos] = useState({ top: 0, right: 8 });

  useEffect(() => {
    if (isOpen && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      const dropW = Math.min(360, window.innerWidth - 16);
      // Align right edge of dropdown to right edge of button, but clamp so it doesn't go off left edge
      const rightFromViewport = window.innerWidth - rect.right;
      const clampedRight = Math.max(8, Math.min(rightFromViewport, window.innerWidth - dropW - 8));
      setDropPos({ top: rect.bottom + 8, right: clampedRight });
    }
  }, [isOpen]);

  const { notifications, unreadCount, loading, markAsRead, markAllAsRead, fetchNotifications } = useNotifications();

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (isOpen) fetchNotifications(activeTab === 'all');
  }, [isOpen, activeTab, fetchNotifications]);

  const handleClick = async (n) => {
    if (!n.is_read) await markAsRead(n.id);
    if (n.actions) {
      const a = typeof n.actions === 'string' ? JSON.parse(n.actions) : n.actions;
      if (a.view) window.location.href = a.view;
    }
    setIsOpen(false);
  };

  const formatTime = (ts) => {
    try {
      return formatDistanceToNow(new Date(ts), { addSuffix: true });
    } catch { return 'recently'; }
  };

  const filtered = activeTab === 'unread' ? notifications.filter(n => !n.is_read) : notifications;

  return (
    <div style={{ position: 'relative', display: 'inline-block' }} ref={dropdownRef}>

      {/* Bell button */}
      <button
        ref={btnRef}
        className="nb-bell-btn"
        onClick={() => setIsOpen(o => !o)}
        style={{
          position: 'relative', cursor: 'pointer',
          background: isOpen ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.12)',
          border: `1px solid ${isOpen ? 'rgba(255,255,255,0.35)' : 'rgba(255,255,255,0.2)'}`,
          borderRadius: 12, padding: '8px 11px',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'all 0.2s ease', backdropFilter: 'blur(8px)',
        }}
      >
        <span style={{
          fontSize: 17, display: 'block', lineHeight: 1,
          animation: unreadCount > 0 ? 'nb-bell 2.5s ease-in-out infinite' : 'none',
          transformOrigin: 'top center',
        }}>🔔</span>
        {unreadCount > 0 && (
          <span style={{
            position: 'absolute', top: -4, right: -4,
            background: 'linear-gradient(135deg, #ef4444, #dc2626)',
            color: '#fff', fontSize: 10, fontWeight: 700,
            minWidth: 18, height: 18, borderRadius: 9,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '0 4px', border: '2px solid rgba(15,23,42,0.8)',
            animation: 'nb-pulse 2s infinite',
          }}>
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div style={{
          position: 'fixed',
          top: dropPos.top,
          right: dropPos.right,
          width: `min(360px, calc(100vw - 16px))`,
          maxHeight: '80vh',
          background: 'linear-gradient(145deg, #0f172a 0%, #1e1b4b 100%)',
          border: '1px solid rgba(99,102,241,0.25)',
          borderRadius: 18, overflow: 'hidden',
          boxShadow: '0 24px 64px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.04)',
          animation: 'nb-drop 0.2s ease-out',
          zIndex: 9999, display: 'flex', flexDirection: 'column',
        }}>

          {/* Header */}
          <div style={{
            padding: '16px 18px 12px',
            borderBottom: '1px solid rgba(255,255,255,0.07)',
            background: 'rgba(99,102,241,0.06)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{
                  width: 28, height: 28, borderRadius: 8,
                  background: 'linear-gradient(135deg, #6366f1, #a78bfa)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 13, boxShadow: '0 4px 12px rgba(99,102,241,0.4)',
                }}>🔔</div>
                <span style={{ fontSize: 15, fontWeight: 700, color: '#fff', letterSpacing: '-0.2px' }}>
                  Notifications
                </span>
                {unreadCount > 0 && (
                  <span style={{
                    fontSize: 11, fontWeight: 700, color: '#818cf8',
                    background: 'rgba(99,102,241,0.2)', border: '1px solid rgba(99,102,241,0.3)',
                    borderRadius: 20, padding: '1px 8px',
                  }}>{unreadCount} new</span>
                )}
              </div>
              {unreadCount > 0 && (
                <button
                  className="nb-mark-btn"
                  onClick={() => { markAllAsRead(); setActiveTab('all'); }}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    fontSize: 11, color: 'rgba(255,255,255,0.4)', fontWeight: 600,
                    padding: '4px 8px', borderRadius: 6, transition: 'color 0.2s',
                  }}>
                  Mark all read
                </button>
              )}
            </div>

            {/* Tabs */}
            <div style={{ display: 'flex', gap: 6 }}>
              {['unread', 'all'].map(tab => (
                <button
                  key={tab}
                  className="nb-tab"
                  onClick={() => setActiveTab(tab)}
                  style={{
                    padding: '5px 14px', borderRadius: 20, cursor: 'pointer',
                    fontSize: 12, fontWeight: 600, border: 'none', transition: 'all 0.2s',
                    background: activeTab === tab ? 'linear-gradient(135deg, #6366f1, #8b5cf6)' : 'rgba(255,255,255,0.06)',
                    color: activeTab === tab ? '#fff' : 'rgba(255,255,255,0.45)',
                    boxShadow: activeTab === tab ? '0 4px 12px rgba(99,102,241,0.35)' : 'none',
                  }}>
                  {tab === 'unread' ? `Unread${unreadCount > 0 ? ` (${unreadCount})` : ''}` : 'All'}
                </button>
              ))}
            </div>
          </div>

          {/* List */}
          <div style={{ overflowY: 'auto', flex: 1, maxHeight: 'min(360px, 50vh)' }}>
            {loading ? (
              <div style={{ padding: '32px 20px', textAlign: 'center' }}>
                <div style={{
                  width: 28, height: 28, borderRadius: '50%', margin: '0 auto 10px',
                  border: '2px solid rgba(99,102,241,0.3)', borderTopColor: '#6366f1',
                  animation: 'spin 0.8s linear infinite',
                }} />
                <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.35)' }}>Loading...</div>
              </div>
            ) : filtered.length === 0 ? (
              <div style={{ padding: '40px 20px', textAlign: 'center' }}>
                <div style={{ fontSize: 36, marginBottom: 10, opacity: 0.4 }}>🔔</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'rgba(255,255,255,0.4)', marginBottom: 4 }}>
                  {activeTab === 'unread' ? 'All caught up!' : 'No notifications yet'}
                </div>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.2)' }}>
                  {activeTab === 'unread' ? 'No unread notifications' : 'Notifications will appear here'}
                </div>
              </div>
            ) : (
              filtered.map((n, i) => {
                const iconColor = TYPE_COLOR[n.type] || '#6366f1';
                const priorityColor = PRIORITY_COLOR[n.priority] || '#6366f1';
                return (
                  <div
                    key={n.id}
                    className="nb-item"
                    onClick={() => handleClick(n)}
                    style={{
                      display: 'flex', gap: 12, padding: '12px 16px',
                      borderBottom: '1px solid rgba(255,255,255,0.04)',
                      cursor: 'pointer', transition: 'background 0.15s',
                      background: !n.is_read ? 'rgba(99,102,241,0.06)' : 'transparent',
                      borderLeft: `3px solid ${!n.is_read ? priorityColor : 'transparent'}`,
                      animation: `nb-drop 0.2s ease-out ${i * 0.03}s both`,
                    }}
                  >
                    {/* Icon */}
                    <div style={{
                      width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                      background: iconColor + '18',
                      border: `1px solid ${iconColor}30`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 15,
                    }}>
                      {TYPE_ICON[n.type] || '🔔'}
                    </div>

                    {/* Content */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 3 }}>
                        <span style={{
                          fontSize: 13, fontWeight: n.is_read ? 500 : 700,
                          color: n.is_read ? 'rgba(255,255,255,0.65)' : '#fff',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>{n.title}</span>
                        <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.25)', flexShrink: 0, marginTop: 1 }}>
                          {formatTime(n.created_at)}
                        </span>
                      </div>
                      <p style={{
                        margin: 0, fontSize: 12, lineHeight: 1.5,
                        color: 'rgba(255,255,255,0.4)',
                        overflow: 'hidden', textOverflow: 'ellipsis',
                        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                      }}>{n.message}</p>
                      {!n.is_read && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 5 }}>
                          <div style={{
                            width: 6, height: 6, borderRadius: '50%',
                            background: priorityColor, boxShadow: `0 0 6px ${priorityColor}`,
                          }} />
                          <span style={{ fontSize: 10, color: priorityColor, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            {n.priority || 'new'}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Footer */}
          <div style={{
            padding: '10px 16px',
            borderTop: '1px solid rgba(255,255,255,0.06)',
            background: 'rgba(0,0,0,0.2)',
            display: 'flex', justifyContent: 'center',
          }}>
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.2)' }}>
              {filtered.length} notification{filtered.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

export default NotificationBell;
